import React, { createContext, useContext, useState, useEffect } from 'react';
import authAPI from '../api/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [accessToken, setAccessToken] = useState(null);

  useEffect(() => {
    // Check if user and token are stored in localStorage
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('accessToken');
    
    if (storedUser && storedToken) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setUserId(userData.id);
        setAccessToken(storedToken);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const response = await authAPI.login(email, password);
      
      // Extract user data and token from the response
      const { access_token, user: userData } = response;
      
      setUser(userData);
      setUserId(userData.id);
      setAccessToken(access_token);
      setIsAuthenticated(true);
      
      // Store both user data and token in localStorage
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('accessToken', access_token);
      
      return userData;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (email, password) => {
    try {
      setIsLoading(true);
      const response = await authAPI.signup(email, password);
      
      // Extract user data and token from the response
      const { access_token, user: userData } = response;
      
      setUser(userData);
      setUserId(userData.id);
      setAccessToken(access_token);
      setIsAuthenticated(true);
      
      // Store both user data and token in localStorage
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('accessToken', access_token);
      
      return userData;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setUserId(null);
    setAccessToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
  };

  const value = {
    user,
    userId,
    accessToken,
    isAuthenticated,
    isLoading,
    login,
    signup,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
